/**
 * When to retry a dropped connection, as a pure decision.
 *
 * Kept apart from the socket so the policy can be tested as a table rather than
 * through timers and fake sockets. Ported from `delayedReconnect` in
 * `web-frontend/modules/core/plugins/realTimeHandler.js`, whose branch order is
 * load-bearing: each early return means something different to the person
 * looking at the tab, and collapsing any two of them changes what they see.
 */

export const RECONNECT_BASE_DELAY = 1000
export const RECONNECT_MAX_DELAY = 30_000
export const RECONNECT_MAX_ATTEMPTS = 10
export const RECONNECT_JITTER = 1000

export interface ReconnectInput {
  /** False once `disconnect()` was called deliberately, or after `force_disconnect`. */
  reconnect: boolean
  /** The page is going away: a navigation or a tab close is in progress. */
  unloading: boolean
  documentHidden: boolean
  online: boolean
  /** Failed attempts so far. Reset to 0 by a successful open or a manual retry. */
  attempts: number
  /** Milliseconds of jitter for this attempt, in `[0, RECONNECT_JITTER)`. */
  jitter: number
}

export type ReconnectPlan =
  /** Nothing to do, and nothing to say on screen. */
  | { readonly action: 'stop' }
  /**
   * Hidden tab. No retry and no banner -- there is nobody to read it, and
   * retrying a background tab on a schedule is how a laptop lid stays warm.
   * `visibilitychange` resumes.
   */
  | { readonly action: 'await-visible' }
  /**
   * Offline, but the tab is in front. Show the banner so the silence is
   * explained, but do not schedule: retrying while the interface is down just
   * burns the attempt budget. The `online` event resumes.
   */
  | { readonly action: 'await-online' }
  /** Out of attempts. The banner becomes "failed", which asks for a reload. */
  | { readonly action: 'give-up' }
  | { readonly action: 'schedule'; readonly attempt: number; readonly delay: number }

/**
 * The delay before attempt `attempt`, counting from 1.
 *
 * Doubles from `RECONNECT_BASE_DELAY`, capped at `RECONNECT_MAX_DELAY`. The cap
 * is applied after the jitter so the total is genuinely bounded; the jitter is
 * what stops every tab of a server that just restarted returning together.
 */
export function reconnectDelay(attempt: number, jitter: number): number {
  const exponent = Math.min(attempt, RECONNECT_MAX_ATTEMPTS) - 1
  return Math.min(RECONNECT_BASE_DELAY * 2 ** exponent + jitter, RECONNECT_MAX_DELAY)
}

export function planReconnect(input: ReconnectInput): ReconnectPlan {
  if (!input.reconnect || input.unloading) return { action: 'stop' }
  if (input.documentHidden) return { action: 'await-visible' }
  if (!input.online) return { action: 'await-online' }

  const attempt = input.attempts + 1
  if (attempt > RECONNECT_MAX_ATTEMPTS) return { action: 'give-up' }

  return { action: 'schedule', attempt, delay: reconnectDelay(attempt, input.jitter) }
}

/**
 * Whether a connection attempt should be abandoned rather than retried.
 *
 * "Failed -- reload" is reserved for a credential the server will keep refusing.
 * A transient network failure has to stay in the retry loop instead, or a tunnel
 * blipping for two seconds tells the user to reload a page that was about to
 * recover on its own.
 */
export function isUnrecoverableAuth(input: {
  token: string | null
  /** The token the last attempt used, whatever became of it. */
  lastToken: string | null
  /** Whether the server has answered the last attempt's `authentication`. */
  authResponseReceived: boolean
  authenticationSuccess: boolean
}): boolean {
  if (input.token === null || input.token === '') return true
  return (
    input.authResponseReceived &&
    !input.authenticationSuccess &&
    input.lastToken === input.token
  )
}
